import { partial } from "ramda";
import { ThunkAction } from "redux-thunk";
import { Maybe } from "true-myth";

import { getDayParts } from "interfaces/day_parts";
import { getLocations } from "interfaces/locations";
import { getSalesForecast } from "interfaces/insights";
import { selectStoreLocation, selectPermalink } from "store/select/account";
import { selectStartEndDates } from "store/select/scheduling";

import { IBrandGroup, IDayPart, ISalesForecast } from "types/account";
import { IRootStateProps } from "types/reducers";
import { RequestStatus } from "types/requests";

export type AccountActionTypes =
  | IReceiveDayPartsAction
  | IReceiveLocationsAction
  | IReceiveSalesForecastAction
  | ISetRequestStatusAction;

type AccountThunkResult<R> = ThunkAction<
  R,
  IRootStateProps,
  undefined,
  AccountActionTypes
>;

export enum AccountTypeKeys {
  SET_REQUEST_STATUS = "account/SET_REQUEST_STATUS",
  RECEIVE_SALES_FORECAST = "account/RECIEVE_SALES_FORECAST",
  RECEIVE_DAY_PARTS = "account/RECEIVE_DAY_PARTS",
  RECEIVE_LOCATIONS = "account/RECEIVE_LOCATIONS",
}

export interface ISetRequestStatusAction {
  type: AccountTypeKeys.SET_REQUEST_STATUS;
  apiRequest: AccountApiRequests;
  status: RequestStatus;
}

enum AccountApiRequests {
  DAY_PARTS_POST = "dayPartsPost",
  LOCATIONS_GET = "locationsGet",
  SALES_FORECAST_GET = "salesForecastGet",
}

function setAccountApiRequest(
  apiRequest: AccountApiRequests,
  status: RequestStatus
): ISetRequestStatusAction {
  return {
    type: AccountTypeKeys.SET_REQUEST_STATUS,
    status,
    apiRequest,
  };
}

const setLocationsGetRequestStatus = partial(setAccountApiRequest, [
  AccountApiRequests.LOCATIONS_GET,
]);
const setDayPartsPostRequestStatus = partial(setAccountApiRequest, [
  AccountApiRequests.DAY_PARTS_POST,
]);
const setForecastGetRequestStatus = partial(setAccountApiRequest, [
  AccountApiRequests.SALES_FORECAST_GET,
]);

function fetchLocations(permalink: string): AccountThunkResult<void> {
  return (dispatch): Promise<void> => {
    dispatch(setLocationsGetRequestStatus(RequestStatus.REQUESTING));
    return getLocations(permalink)
      .then((locations: IBrandGroup[]) => {
        dispatch(receiveLocations(permalink, locations));
        dispatch(setLocationsGetRequestStatus(RequestStatus.SUCCESS));
      })
      .catch(() => {
        dispatch(setLocationsGetRequestStatus(RequestStatus.FAILURE));
      });
  };
}

export interface IReceiveLocationsAction {
  type: AccountTypeKeys.RECEIVE_LOCATIONS;
  permalink: string;
  locations: IBrandGroup[];
}

function receiveLocations(
  permalink: string,
  locations: IBrandGroup[]
): IReceiveLocationsAction {
  return {
    type: AccountTypeKeys.RECEIVE_LOCATIONS,
    locations,
    permalink,
  };
}

function fetchDayParts(storePrettyUrl: string): AccountThunkResult<void> {
  return (dispatch, getState) => {
    const storeLocation = selectStoreLocation(getState());
    storeLocation.map((location) => {
      dispatch(setDayPartsPostRequestStatus(RequestStatus.REQUESTING));
      return getDayParts(location.brandId, storePrettyUrl)
        .then((dayParts: IDayPart[]) => {
          dispatch(setDayPartsPostRequestStatus(RequestStatus.SUCCESS));
          dispatch(receiveDayParts(dayParts));
        })
        .catch(() => {
          dispatch(setDayPartsPostRequestStatus(RequestStatus.FAILURE));
        });
    });
  };
}

function fetchSalesForecast(storePrettyUrl: string): AccountThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const permalink = selectPermalink(state);
    const { startDate, endDate } = selectStartEndDates(state);
    Maybe.all(permalink, startDate, endDate).match({
      Just: ([permalinkString, start, end]) => {
        dispatch(setForecastGetRequestStatus(RequestStatus.REQUESTING));
        getSalesForecast(
          permalinkString as string,
          storePrettyUrl,
          start as Date,
          end as Date
        )
          .then((salesForecast: ISalesForecast) => {
            dispatch(setForecastGetRequestStatus(RequestStatus.SUCCESS));
            dispatch(receiveSalesForecast(salesForecast));
          })
          .catch(() => {
            dispatch(setForecastGetRequestStatus(RequestStatus.FAILURE));
          });
      },
      Nothing: () => {
        dispatch(setForecastGetRequestStatus(RequestStatus.FAILURE));
      },
    });
  };
}

interface IReceiveSalesForecastAction {
  type: AccountTypeKeys.RECEIVE_SALES_FORECAST;
  salesForecast: ISalesForecast;
}

function receiveSalesForecast(
  salesForecast: ISalesForecast
): IReceiveSalesForecastAction {
  return {
    type: AccountTypeKeys.RECEIVE_SALES_FORECAST,
    salesForecast,
  };
}

export interface IReceiveDayPartsAction {
  type: AccountTypeKeys.RECEIVE_DAY_PARTS;
  dayParts: IDayPart[];
}

function receiveDayParts(dayParts: IDayPart[]): IReceiveDayPartsAction {
  return {
    type: AccountTypeKeys.RECEIVE_DAY_PARTS,
    dayParts,
  };
}

export {
  fetchDayParts,
  fetchLocations,
  fetchSalesForecast,
  setAccountApiRequest,
};
